import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material/material.module';
import { Subject } from '../../../middle-school-math/models';

export interface SubjectDialogData {
  subject?: Subject;
  isNew: boolean;
}

@Component({
  selector: 'app-admin-subject-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <div class="title-wrap">
          <div class="subject-icon-preview" [style.background]="subject.color || '#1976d2'">
            <mat-icon>{{ subject.icon || 'category' }}</mat-icon>
          </div>
          <div>
            <h2 mat-dialog-title class="dialog-title">{{ data.isNew ? 'Add New Subject' : 'Edit Subject' }}</h2>
            <p class="dialog-subtitle">{{ subject.name || 'New Subject Category' }}</p>
          </div>
        </div>
        <button mat-icon-button (click)="cancel()" aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-content class="dialog-body">
        <div class="form-grid">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Subject Name</mat-label>
            <input matInput [(ngModel)]="subject.name" (ngModelChange)="onNameChange()" placeholder="e.g. Science, Mathematics, Computer Science" required />
            <mat-hint>The top-level subject category name</mat-hint>
          </mat-form-field>

          <div class="field-with-btn full-width">
            <mat-form-field appearance="outline" class="flex-1">
              <mat-label>Subject ID / Slug</mat-label>
              <input matInput [(ngModel)]="subject.id" placeholder="e.g. science, math, computer_science" required [disabled]="!data.isNew && !allowIdEdit" />
              <mat-hint>Unique URL-safe identifier</mat-hint>
            </mat-form-field>
            <button mat-stroked-button color="primary" type="button" (click)="autoGenerateSlug()" *ngIf="data.isNew || allowIdEdit">
              <mat-icon>auto_fix_high</mat-icon> Generate
            </button>
            <button mat-icon-button type="button" (click)="allowIdEdit = !allowIdEdit" *ngIf="!data.isNew" matTooltip="Unlock ID edit">
              <mat-icon>{{ allowIdEdit ? 'lock_open' : 'lock' }}</mat-icon>
            </button>
          </div>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea matInput [(ngModel)]="subject.description" placeholder="Short description of this subject area" rows="2"></textarea>
          </mat-form-field>

          <!-- Icon Selection -->
          <div class="full-width selection-group">
            <label class="group-label">Select Icon</label>
            <div class="icon-presets">
              <button *ngFor="let ic of popularIcons" 
                      type="button" 
                      class="icon-btn" 
                      [class.active]="subject.icon === ic" 
                      (click)="subject.icon = ic">
                <mat-icon>{{ ic }}</mat-icon>
              </button>
            </div>
            <mat-form-field appearance="outline" style="width: 200px; margin-top: 0.5rem;">
              <mat-label>Custom Material Icon</mat-label>
              <input matInput [(ngModel)]="subject.icon" placeholder="e.g. calculate" />
            </mat-form-field>
          </div>

          <!-- Color Selection -->
          <div class="full-width selection-group">
            <label class="group-label">Theme Color</label>
            <div class="color-presets">
              <button *ngFor="let col of colorPresets" 
                      type="button" 
                      class="color-btn" 
                      [style.background]="col" 
                      [class.active]="subject.color === col" 
                      (click)="subject.color = col">
                <mat-icon *ngIf="subject.color === col" class="check-icon">check</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-actions class="dialog-actions">
        <button mat-stroked-button color="warn" type="button" *ngIf="!data.isNew" (click)="delete()">
          <mat-icon>delete_outline</mat-icon> Delete Subject
        </button>
        <span class="spacer"></span>
        <button mat-button type="button" (click)="cancel()">Cancel</button>
        <button mat-raised-button color="primary" type="button" (click)="save()">
          <mat-icon>save</mat-icon> {{ data.isNew ? 'Create Subject' : 'Save Subject' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 520px;
    }
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.25rem 1.5rem;
      background: #fafafa;
    }
    .title-wrap {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .subject-icon-preview {
      height: 48px;
      width: 48px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      box-shadow: 0 4px 10px rgba(0,0,0,0.15);
    }
    .subject-icon-preview mat-icon {
      font-size: 28px;
      height: 28px;
      width: 28px;
    }
    .dialog-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
    }
    .dialog-subtitle {
      margin: 0.2rem 0 0 0;
      font-size: 0.85rem;
      color: #666;
    }
    .dialog-body {
      padding: 1.25rem 1.5rem;
    }
    .form-grid {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .full-width { width: 100%; }
    .field-with-btn {
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
    }
    .flex-1 { flex: 1; }

    .selection-group {
      margin-top: 0.5rem;
    }
    .group-label {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
    }
    .icon-presets, .color-presets {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .icon-btn {
      height: 40px;
      width: 40px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #475569;
      transition: all 0.2s;
    }
    .icon-btn:hover {
      background: #e2e8f0;
    }
    .icon-btn.active {
      background: #1976d2;
      color: white;
      border-color: #1565c0;
    }
    .color-btn {
      height: 36px;
      width: 36px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 0 0 1px #cbd5e1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s;
    }
    .color-btn:hover {
      transform: scale(1.1);
    }
    .color-btn.active {
      box-shadow: 0 0 0 3px #1976d2;
    }
    .check-icon {
      font-size: 18px;
      height: 18px;
      width: 18px;
      color: white;
    }
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      background: #fafafa;
    }
    .spacer { flex: 1; }
  `]
})
export class AdminSubjectDialogComponent implements OnInit {
  subject: Subject;
  allowIdEdit = false;

  popularIcons = ['calculate', 'science', 'psychology', 'menu_book', 'biotech', 'functions', 'history_edu', 'computer', 'public'];
  colorPresets = ['#1976d2', '#00897b', '#7c3aed', '#e65100', '#d32f2f', '#0284c7', '#059669', '#475569'];

  constructor(
    public dialogRef: MatDialogRef<AdminSubjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SubjectDialogData
  ) {
    if (data.subject) {
      this.subject = JSON.parse(JSON.stringify(data.subject));
    } else {
      this.subject = {
        id: '',
        name: '',
        description: '',
        icon: 'science',
        color: '#00897b',
        displayOrder: 1
      };
    }
  }

  ngOnInit() {}

  onNameChange() {
    if (this.data.isNew && (!this.subject.id || this.subject.id.trim() === '')) {
      this.autoGenerateSlug();
    }
  }

  autoGenerateSlug() {
    if (this.subject.name) {
      this.subject.id = this.subject.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }
  }

  delete() {
    if (confirm(`Are you sure you want to delete Subject "${this.subject.name}" (${this.subject.id})?\n\nWARNING: This will permanently delete this subject, all books inside it, and all chapters within those books!`)) {
      this.dialogRef.close({ delete: true, id: this.subject.id });
    }
  }

  save() {
    if (!this.subject.name || this.subject.name.trim() === '') {
      alert('Subject Name is required');
      return;
    }
    if (!this.subject.id || this.subject.id.trim() === '') {
      alert('Subject ID is required');
      return;
    }
    this.dialogRef.close(this.subject);
  }

  cancel() {
    this.dialogRef.close();
  }
}
